"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Sparkles, Eye, EyeOff, Loader2, UserPlus } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";

export default function LoginPage() {
  const router = useRouter();
  const { login } = useAuth();
  const [tenantId, setTenantId] = useState("sys-admin");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(tenantId, username, password);
      router.push("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败，请检查账号密码");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-stellar-gold/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-primary/5 blur-3xl" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle, #000 1px, transparent 1px)`,
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-stellar-gold/20 to-stellar-gold/5 border border-stellar-gold/30 mb-4">
            <Sparkles className="w-8 h-8 text-stellar-gold" />
          </div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">天枢超级底座</h1>
          <p className="text-sm text-muted-foreground mt-1">中科华微产业投资集团 · 天枢数字底座规范</p>
        </div>

        {/* Login card */}
        <div className="bg-white rounded-2xl shadow-sm border border-border/50 p-8">
          <h2 className="text-lg font-semibold text-foreground mb-6">登录</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Tenant ID */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                租户标识
              </label>
              <input
                type="text"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30 focus:border-stellar-gold transition-colors"
                placeholder="请输入租户标识"
                required
              />
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                用户名
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30 focus:border-stellar-gold transition-colors"
                placeholder="请输入用户名"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                密码
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-stellar-gold/30 focus:border-stellar-gold transition-colors"
                  placeholder="请输入密码"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-lg bg-stellar-gold text-white font-medium text-sm hover:bg-stellar-gold/90 focus:outline-none focus:ring-2 focus:ring-stellar-gold/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  登录中...
                </>
              ) : (
                "登录"
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/30"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs text-muted-foreground">或</span>
            </div>
          </div>

          {/* Register new tenant */}
          <button
            type="button"
            onClick={() => router.push("/admin/governance?tab=register")}
            className="w-full py-2.5 px-4 rounded-lg border border-stellar-gold/30 text-stellar-gold font-medium text-sm hover:bg-stellar-gold/5 focus:outline-none focus:ring-2 focus:ring-stellar-gold/20 transition-colors flex items-center justify-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            新增租户
          </button>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          中科华微产业投资集团 · 天枢数字底座规范
        </p>
      </div>
    </div>
  );
}
